// CollectionLedCategory, the Edit's collection-led category experience.
// Journey: Hero -> Explore by Mood -> Featured Collections -> Editor's Pick ->
// Browse the Collection (Refine + grid). Collection cards open an editorial
// collection view (?collection=slug), not a filtered search. Collections & moods
// read the styleTags/locationTags already on each feature (no new content model).
// Hero is media-agnostic (image now; video/carousel later) and rotates daily.
import { useState, useMemo, useRef, useEffect } from 'react';
import { imgUrl } from '../../utils/imgUrl';

const FD = "'Gilda Display', 'Playfair Display', Georgia, serif";
const FU = "'Nunito', 'Inter', 'Helvetica Neue', sans-serif";

const lc = s => (s || '').toLowerCase();
const hasTag = (p, t) => (p.styleTags || []).some(x => lc(x) === lc(t));
const hasAny = (p, arr) => arr.some(t => hasTag(p, t));
const countryOf = p => { const l = p.locationTags || []; return l[l.length - 1] || ''; };
const locLabel = p => { const l = p.locationTags || []; if (!l.length) return ''; const a = l[0], b = l[l.length - 1]; return a && b && a !== b ? `${a}, ${b}` : (b || a); };
const dayIndex = () => { const n = new Date(); return Math.floor((Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())) / 86400000); };
const CDN = id => `https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/listing-media/${id}.jpg`;

// Curated hero pools per category (landscape, dramatic, daily-rotated). Venues =
// grand places; Creatives = editorial wedding scenes that showcase the craft.
const HERO_POOLS = {
  'our-venues': [
    'b1c3cb76-17ef-4e11-9b92-c9ee54e100dd', // Leeds Castle, moat
    'bc84ee6b-24b2-4589-bb84-de51e271142f', // Inverlochy Castle, loch + Ben Nevis
    '01ec46ca-ec6b-4d48-ae64-4209c4f73de7', // Villa Mangiacane, Chianti
    'bb1cef72-6b9b-421d-bc12-7cf3039a6e65', // Chateau de Ferrieres
    'c0b90de9-64ea-4b8b-9f39-31ab9f69836d', // Castello di Velona, Val d'Orcia
    '6f9209f9-67bc-47e0-a6fc-970a337f29d6', // Oheka Castle, formal gardens
    'd70574ee-146e-4ecd-b0da-e16fb5662aa6', // Villa del Balbianello, Loggia Durini
    '0c70c341-6a2b-4c01-9b99-b7c11bf910b4', // Thornbury Castle, Tudor aerial
    '1b64a626-bb6c-48bc-9c73-a7f262562237', // Vila Bled, Lake Bled
  ],
  'our-creatives': [
    '0d8da969-9f13-47f1-ab2a-026f6bd1b306', // Balbianello lake-terrace reception
    '1eaec3d4-483e-4f3e-bef3-e509160a4771', // Balbianello, Loggia Segre wedding dinner
    '43879c5f-eac1-4634-96ed-71872c982046', // Villa Michaela, long table under olive trees
  ],
  'real-weddings': [
    '0d8da969-9f13-47f1-ab2a-026f6bd1b306', // Balbianello lake-terrace reception
    '1eaec3d4-483e-4f3e-bef3-e509160a4771', // Balbianello, Loggia Segre wedding dinner
    '43879c5f-eac1-4634-96ed-71872c982046', // Villa Michaela, long table under olive trees
  ],
  'destinations': [
    '19f527de-8df7-46c6-9d3a-20a062ffc920', // Amalfi coast
    '1b171019-3011-4ee5-b367-4797602ba2de', // Umbria, golden hour
    '33249bd7-1732-4a2b-87b7-5844e659a4c0', // Tuscany
  ],
  'perspectives': [
    'c0b90de9-64ea-4b8b-9f39-31ab9f69836d', // Wellness (Castello di Velona)
    '7b625df8-d8c0-497a-b0e4-00ee67493ebb', // Quiet Luxury (estate aerial)
    'dc32c857-54ce-498f-acca-6d3c9c653434', // Wedding Weekend (long table)
    '4206e5aa-45d1-4040-94e2-01af4f1a62a8', // Design-Led (architectural interior)
  ],
  'fashion': [
    '3e07463d-7123-4e3f-8d13-775e0f39953b', // The Amalfi Edit
  ],
};

// ── Collections (editorial). Matchers over styleTags/location. No Hidden Gems
//    until it's a real, curated collection (P2). ──────────────────────────────
const COLLECTIONS = [
  { slug: 'castle-weddings',    name: 'Castle Weddings',          intro: 'Ancient walls and modern celebrations. The venues where history holds the day.',        match: p => hasTag(p, 'Castle') },
  { slug: 'italian-villas',     name: 'Italian Villas',           intro: 'The romance of the Italian estate, from Lake Como to the Tuscan hills.',                match: p => lc(countryOf(p)) === 'italy' && hasAny(p, ['Villa', 'Borgo', 'Wine Estate']) },
  { slug: 'coastal-escapes',    name: 'Coastal Escapes',          intro: 'Weddings by the water, where the light does half the work.',                            match: p => hasAny(p, ['Coastal', 'Sea View', 'Beachfront', 'Beach', 'Island']) },
  { slug: 'historic-estates',   name: 'Historic Estates & Houses', intro: 'Centuries of grandeur, kept and quietly extraordinary.',                                match: p => hasTag(p, 'Historic') && hasAny(p, ['Country Estate', 'Country House', 'Estate', 'Castle', 'Georgian']) },
  { slug: 'grand-hotels',       name: 'Grand Hotels',             intro: 'Five-star ballrooms, butler service, and a century of occasion.',                       match: p => hasAny(p, ['Grand Hotel', 'Five-Star', 'Ballroom', 'Hotel']) },
  { slug: 'editors-picks',      name: "Editor's Picks",           intro: 'The features our editors return to. Chosen, not ranked.',                               match: p => p.editors_choice === true || p.editorial_priority != null, kind: 'editor' },
  { slug: 'recently-published', name: 'Recently Published',       intro: 'The newest additions to the collection.',                                              match: () => true, kind: 'recent' },
];

// ── Moods (venues only). Icon + label, evenly spaced. ─────────────────────────
const I = { s: 1.6, w: 24 };
const Ic = {
  Romantic:   <path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5C19 15.5 12 20 12 20z" />,
  Grand:      <path d="M4 8l3 4 5-6 5 6 3-4v9H4z" />,
  Coastal:    <><path d="M3 9c2 0 2 1.5 4.5 1.5S10 9 12 9s2.5 1.5 4.5 1.5S21 9 21 9" /><path d="M3 14c2 0 2 1.5 4.5 1.5S10 14 12 14s2.5 1.5 4.5 1.5S21 14 21 14" /></>,
  Historic:   <><path d="M4 20h16M6 20V9m4 11V9m4 11V9m4 11V9M3 9l9-5 9 5" /></>,
  Modern:     <path d="M12 3l9 9-9 9-9-9z" />,
  Intimate:   <><circle cx="8" cy="9" r="2.5" /><circle cx="16" cy="9" r="2.5" /><path d="M4 19c0-2.5 2-4 4-4M20 19c0-2.5-2-4-4-4" /></>,
  Countryside:<><path d="M12 20V8M12 8c0-2 1.5-3.5 3.5-3.5M12 12c0-1.5 1.2-2.8 2.8-2.8M12 8c0-2-1.5-3.5-3.5-3.5M12 12c0-1.5-1.2-2.8-2.8-2.8" /></>,
  Mountain:   <path d="M3 19l6-11 4 6 2-3 6 8z" />,
  Island:     <><path d="M12 20c0-6 0-9 0-9M12 11c-1-3-4-4-7-3 3 0 5 1.5 7 3zM12 11c1-3 4-4 7-3-3 0-5 1.5-7 3zM12 11c-2-2.5-2-5 0-8 2 3 2 5.5 0 8z" /></>,
  Garden:     <><circle cx="12" cy="9" r="2.5" /><path d="M12 11v9M9 15l-3-1M15 15l3-1" /></>,
  City:       <><path d="M4 20V6h7v14M11 20V10h9v10M7 9h1M7 13h1M14 13h1M17 13h1M14 17h1M17 17h1" /></>,
};
const MoodIcon = ({ name, color, size = I.w }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">{Ic[name]}</svg>
);
const MOODS = [
  { label: 'Romantic',    tags: ['Villa', 'Garden', 'Historic', 'Borgo'] },
  { label: 'Grand',       tags: ['Five-Star', 'Ballroom', 'Grand Hotel', 'Castle'] },
  { label: 'Coastal',     tags: ['Coastal', 'Sea View', 'Beachfront', 'Beach'] },
  { label: 'Historic',    tags: ['Historic', 'Georgian'] },
  { label: 'Modern',      tags: ['Resort', 'Rooftop', 'City'] },
  { label: 'Intimate',    tags: ['Intimate', 'Borgo', 'Masia'] },
  { label: 'Countryside', tags: ['Country House', 'Country Estate', 'Country', 'Barn'] },
  { label: 'Mountain',    tags: ['Mountain', 'Alpine'] },
  { label: 'Island',      tags: ['Island', 'Sea View'] },
  { label: 'Garden',      tags: ['Garden', 'Gardens'] },
  { label: 'City',        tags: ['City', 'Rooftop'] },
];

const VENUE_TYPES = ['Villa', 'Castle', 'Hotel', 'Grand Hotel', 'Country House', 'Country Estate', 'Estate', 'Resort', 'Palazzo', 'Borgo', 'Masia', 'Wine Estate'];

// Turn a stored filter_rules blob into a live (kind, match) pair over posts.
function ruleToMatch(fr) {
  const type = fr?.collection_type || 'tag';
  const m = fr?.match || {};
  if (type === 'recent') return { kind: 'recent', match: () => true };
  if (type === 'editor') return { kind: 'editor', match: p => p.editors_choice === true || p.editorial_priority != null };
  return { kind: 'tag', match: p => {
    if (m.country && lc(countryOf(p)) !== lc(m.country)) return false;
    if (m.requireTags && !m.requireTags.every(t => hasTag(p, t))) return false;
    if (m.tags && m.tags.length && !hasAny(p, m.tags)) return false;
    return true;
  } };
}

// ── Article cards must be REAL anchors, not click handlers ───────────────────
// Found 2026-08-15: every article card on every category hub was a <button>,
// <div> or <article> with onClick and no href. Humans were fine, because
// JavaScript ran. Crawlers were not: a full internal-link audit found 381
// article cards across six hubs (our-venues 300, our-creatives 35, editorial 23,
// fashion 12, destinations 7, perspectives 4) emitting ZERO crawlable links.
// The articles sit in the sitemap, so they are discoverable, but nothing on the
// site linked to them, so they read as orphans carrying no internal authority.
//
// `hrefFor` comes from the PARENT on purpose. This component must not decide
// routing: editorial articles live at /lwd-edit/<slug> and legacy magazine
// articles at /magazine/<slug>, and hardcoding either here is exactly the
// "content decides routing" mistake that produced the stale lazio/campania URLs
// elsewhere. No hrefFor means no href, so nothing breaks for a caller that has
// not been updated; it just keeps the old click-only behaviour.
//
// The click handler is preserved so SPA navigation still works, and it bails on
// modified clicks so cmd-click, middle-click and "open in new tab" behave like
// real links now that they are real links.
export default function CollectionLedCategory({ category, posts: rawPosts = [], chapterImages = {}, collectionsData = null, onArticle, onNavigateCategory, hrefFor, isLight = true }) {
  const feedRef = useRef(null);
  const isVenues   = lc(category?.slug) === 'our-venues' || lc(category?.label) === 'our-venues';
  const isCreatives = lc(category?.slug) === 'our-creatives' || lc(category?.label) === 'our-creatives';
  const isGeneric  = !isVenues && !isCreatives; // Real Weddings / Destinations / Perspectives / Fashion

  // Tolerant of mapped (camelCase) and raw db (snake_case) post shapes.
  const posts = useMemo(() => (rawPosts || []).map(p => ({
    ...p,
    coverImage:   p.coverImage   || p.cover_image,
    styleTags:    p.styleTags    || p.style_tags    || [],
    locationTags: p.locationTags || p.location_tags || [],
    excerpt:      p.excerpt || p.standfirst || '',
    creativeType: (p.secondary_categories || p.secondaryCategories || [])[0] || null,
  })), [rawPosts]);

  // active collection (editorial view) + refine state, initialised from URL
  const initialCollection = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('collection') : null;
  const [activeCollection, setActiveCollection] = useState(initialCollection);
  const [mood, setMood]       = useState(null);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refine, setRefine]   = useState({ country: '', venueType: '', creativeType: '', style: '', exclusive: '', search: '' });

  const openCollection = c => { setActiveCollection(c.slug); setMood(null); try { const u = new URL(window.location.href); u.searchParams.set('collection', c.slug); window.history.pushState({}, '', u); } catch {} window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const closeCollection = () => { setActiveCollection(null); try { const u = new URL(window.location.href); u.searchParams.delete('collection'); window.history.pushState({}, '', u); } catch {} };
  useEffect(() => { const on = () => setActiveCollection(new URLSearchParams(window.location.search).get('collection')); window.addEventListener('popstate', on); return () => window.removeEventListener('popstate', on); }, []);

  // ── theme (ivory light / dark) ──
  const T = isLight ? {
    BG: '#f6f2ea', PANEL: '#fffdf8', BAND: '#efe9dd', TEXT: '#1b1710', MUTED: 'rgba(27,23,16,0.62)',
    FAINT: 'rgba(27,23,16,0.44)', LINE: 'rgba(27,23,16,0.12)', GOLD: '#a9823f', CHIP: '#fffdf8',
  } : {
    BG: '#0a0a0a', PANEL: '#0d0d0b', BAND: '#111', TEXT: '#f5f0e8', MUTED: 'rgba(245,240,232,0.6)',
    FAINT: 'rgba(245,240,232,0.42)', LINE: 'rgba(245,240,232,0.12)', GOLD: '#c9a96e', CHIP: 'rgba(245,240,232,0.05)',
  };

  // hero image pool + daily rotation (media-agnostic; image only for now)
  const heroPool = useMemo(() => {
    const curated = HERO_POOLS[lc(category?.slug)];
    if (curated && curated.length) return curated.map(CDN);
    const pool = [category?.heroImage, ...posts.slice(0, 12).map(p => p.coverImage)].filter(Boolean);
    return [...new Set(pool)];
  }, [category, posts]);
  const heroImage = heroPool.length ? heroPool[dayIndex() % heroPool.length] : category?.heroImage;

  // Unified collection defs: real editorial_collections rows when present,
  // otherwise the built-in tag-matchers (so the page never depends on the DB).
  const collectionDefs = useMemo(() => {
    if (collectionsData && collectionsData.length) {
      return [...collectionsData]
        .sort((a, b) => (a.filter_rules?.display_order ?? 99) - (b.filter_rules?.display_order ?? 99))
        .map(c => {
          const { kind, match } = ruleToMatch(c.filter_rules);
          return { slug: c.slug, name: c.title, intro: c.subtitle || '', editorialIntro: c.editorial_intro || '', heroImage: c.hero_image_url || null, kind, match };
        });
    }
    return COLLECTIONS.map(c => ({ slug: c.slug, name: c.name, intro: c.intro, editorialIntro: '', heroImage: null, kind: c.kind || 'tag', match: c.match }));
  }, [collectionsData]);

  // collection cards (with rep image + count), hide empties
  const collections = useMemo(() => collectionDefs.map(c => {
    const members = c.kind === 'recent' ? posts : posts.filter(c.match);
    const rep = c.kind === 'recent' ? [...posts].sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0))[0] : members[0];
    return { ...c, count: members.length, image: c.heroImage || rep?.coverImage };
  }).filter(c => c.count >= 3), [collectionDefs, posts]);

  const activeColl = collectionDefs.find(c => c.slug === activeCollection);
  const collectionPosts = useMemo(() => {
    if (!activeColl) return posts;
    if (activeColl.kind === 'recent') return [...posts].sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));
    return posts.filter(activeColl.match);
  }, [posts, activeColl]);

  // Editor's Pick, real curation, in priority order (never "latest" as the driver):
  //   1. editors_choice = true (hand-picked by the editor)
  //   2. then editorial_priority (ranked)
  //   3. then best image + excerpt quality, to top up to 4
  const picks = useMemo(() => {
    const chosen  = posts.filter(p => p.editors_choice === true);
    const seen    = new Set(chosen);
    const ranked  = posts.filter(p => !seen.has(p) && p.editorial_priority != null)
      .sort((a, b) => a.editorial_priority - b.editorial_priority);
    ranked.forEach(p => seen.add(p));
    const quality = posts.filter(p => !seen.has(p) && p.coverImage && (p.excerpt || '').length > 40);
    return [...chosen, ...ranked, ...quality].slice(0, 4);
  }, [posts]);

  // refine option lists (from real tag data)
  const countries = useMemo(() => [...new Set(posts.map(countryOf).filter(Boolean))].sort(), [posts]);
  const styles    = useMemo(() => [...new Set(posts.flatMap(p => p.styleTags))].sort(), [posts]);
  const creativeTypes = useMemo(() => [...new Set(posts.map(p => p.creativeType).filter(Boolean))].sort(), [posts]);

  // final feed = collectionPosts filtered by mood + refine + search
  const feed = useMemo(() => collectionPosts.filter(p => {
    if (mood && !hasAny(p, mood.tags)) return false;
    if (refine.country && lc(countryOf(p)) !== lc(refine.country)) return false;
    if (refine.venueType && !hasTag(p, refine.venueType)) return false;
    if (refine.creativeType && lc(p.creativeType) !== lc(refine.creativeType)) return false;
    if (refine.style && !hasTag(p, refine.style)) return false;
    if (refine.exclusive === 'yes' && !hasTag(p, 'Exclusive Use')) return false;
    if (refine.search) { const q = lc(refine.search); if (!(lc(p.title).includes(q) || lc(p.excerpt).includes(q) || lc(locLabel(p)).includes(q))) return false; }
    return true;
  }), [collectionPosts, mood, refine]);

  const go = p => onArticle && onArticle(p.slug || p);
  // href for a post, or undefined when the parent supplied no router.
  const hrefOf = p => (hrefFor ? hrefFor(p?.slug || p) : undefined);
  // Left-click without modifiers navigates in-app; everything else (cmd/ctrl/
  // shift/middle-click) falls through to the browser so the anchor behaves like
  // an anchor. Guard on onArticle so an href-only caller still works.
  //
  // A card with NO href is INERT: no navigation at all, and no pointer cursor.
  // Gating the href alone was half a fix. It stopped Google following the card,
  // and left the JavaScript click intact, so a person clicking a gated card still
  // travelled to a page that does not exist and got a 404. Found on the
  // Capovaticano card, a cold prospect whose article is deliberately unpublished.
  //
  // `hrefFor` supplying no URL is the single signal for "this has no public
  // destination", so the click and the link can never disagree about it.
  const isInert = p => (hrefFor ? !hrefOf(p) : false);
  const cardNav = p => (e) => {
    if (isInert(p)) { e.preventDefault(); return; }
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (!onArticle) return;
    e.preventDefault();
    go(p);
  };
  const cardCursor = p => (isInert(p) ? 'default' : 'pointer');
  const total = posts.length;
  const ctaLabel = isCreatives ? `Explore ${total} Creative ${total === 1 ? 'Feature' : 'Features'}`
    : `Explore ${total} Editorial ${total === 1 ? 'Feature' : 'Features'}`;
  const scrollFeed = () => setTimeout(() => feedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 40);
  const featureNoun = isCreatives ? 'Creative' : 'Feature';

  return (
    <div style={{ background: T.BG, color: T.TEXT }}>
      <style>{`
        @keyframes clcFade { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .clc-card-img { transition: transform 0.7s cubic-bezier(0.16,1,0.3,1); }
        .clc-card:hover .clc-card-img, .clc-coll:hover .clc-card-img { transform: scale(1.04); }
        .clc-card { transition: transform 0.32s cubic-bezier(0.16,1,0.3,1); }
        .clc-card:hover { transform: translateY(-3px); }
        .clc-coll { transition: transform 0.32s cubic-bezier(0.16,1,0.3,1); }
        .clc-coll:hover { transform: translateY(-3px); }
        .clc-hero-content { animation: clcRise 0.9s cubic-bezier(0.16,1,0.3,1) both; }
        @keyframes clcRise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .clc-hscroll { scrollbar-width: none; -ms-overflow-style: none; }
        .clc-hscroll::-webkit-scrollbar { display: none; }
        .clc-mood { transition: transform 0.22s ease, color 0.2s ease; }
        .clc-mood:hover { transform: translateY(-3px); }
        .clc-mood-ul { width: 0; height: 2px; background: ${T.GOLD}; margin-top: 4px; transition: width 0.3s cubic-bezier(0.16,1,0.3,1); }
        .clc-mood:hover .clc-mood-ul { width: 22px; }
        .clc-read { transition: gap 0.2s ease; }
        .clc-card:hover .clc-read { gap: 12px; }
        .clc-chapter { transition: color 0.22s ease; }
        .clc-chapter:hover { color: ${T.GOLD} !important; }
        .clc-coll-lead { aspect-ratio: 16 / 7.8; }
        @media (max-width: 760px) {
          .clc-hero { height: 80svh !important; }
          /* Reduce text on mobile: the hero standfirst is hidden (house pattern),
             so the mobile hero reads eyebrow + title + meta + CTA, uncluttered. */
          .clc-hero-desc { display: none !important; }
          .clc-mood-row { justify-content: flex-start !important; }
          .clc-editor { grid-template-columns: 1fr !important; }
          .clc-editor-rail { border-left: none !important; padding-left: 0 !important; }
          .clc-coll-grid { grid-template-columns: 1fr !important; }
          .clc-coll-lead { aspect-ratio: 4 / 3 !important; }
          .clc-chapters { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ══ COLLECTION VIEW (editorial) ══ */}
      {activeColl ? (
        <>
          <section className="clc-hero" style={{ position: 'relative', height: 'clamp(360px, 56svh, 560px)', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imgUrl(activeColl.heroImage || collectionPosts[0]?.coverImage || heroImage, { width: 1800, resize: 'contain' })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.32) 100%)' }} />
            <div style={{ position: 'relative', height: '100%', maxWidth: 1180, margin: '0 auto', padding: '0 clamp(24px, 6vw, 80px) clamp(40px, 6vw, 72px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <button onClick={closeCollection} style={{ alignSelf: 'flex-start', fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.75)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 18, padding: 0 }}>← {category.label}</button>
              <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 14 }}>The Collection</div>
              <h1 className="clc-hero-content" style={{ fontFamily: FD, fontSize: 'clamp(40px, 6vw, 78px)', fontWeight: 400, color: '#f5f0e8', margin: '0 0 16px', lineHeight: 1.03 }}>{activeColl.name}</h1>
              <p style={{ fontFamily: FU, fontSize: 'clamp(15px, 1.4vw, 17px)', fontWeight: 300, color: 'rgba(245,240,232,0.82)', margin: 0, lineHeight: 1.7, maxWidth: 600 }}>{activeColl.intro}</p>
            </div>
          </section>
          {/* Long-form editorial intro, the landing-page body */}
          {activeColl.editorialIntro && (
            <section style={{ background: T.BG, padding: 'clamp(44px, 6vw, 76px) clamp(24px, 5vw, 80px) clamp(8px, 1vw, 16px)' }}>
              <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
                <p style={{ fontFamily: FD, fontSize: 'clamp(19px, 1.9vw, 25px)', fontWeight: 400, color: T.TEXT, margin: 0, lineHeight: 1.72 }}>{activeColl.editorialIntro}</p>
              </div>
            </section>
          )}
          <FeedSection title={`In this Collection`} count={collectionPosts.length} noun={featureNoun} feedRef={feedRef} feed={collectionPosts} onGo={go} hrefOf={hrefOf} onCardNav={cardNav} T={T} />
        </>
      ) : (
        <>
          {/* ══ HERO ══ */}
          <section className="clc-hero" style={{ position: 'relative', height: 'clamp(560px, 90svh, 920px)', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imgUrl(heroImage, { width: 1800 })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.30) 48%, rgba(0,0,0,0.46) 100%), linear-gradient(105deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.12) 48%, transparent 72%)' }} />

            {/* masthead, top-left authority line */}
            <div style={{ position: 'absolute', top: 'clamp(24px, 4vw, 46px)', left: 0, right: 0 }}>
              <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 clamp(28px, 7vw, 104px)' }}>
                <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.36em', textTransform: 'uppercase', color: '#f5f0e8', marginBottom: 10 }}>The LWD Edit</div>
                <div style={{ width: 26, height: 1, background: T.GOLD, marginBottom: 10 }} />
                <div style={{ fontFamily: FU, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.6)' }}>An LWD Publication · Curated since 2026</div>
              </div>
            </div>

            <div style={{ position: 'relative', height: '100%', maxWidth: 1240, margin: '0 auto', padding: '0 clamp(28px, 7vw, 104px) clamp(56px, 8vw, 108px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div className="clc-hero-content" style={{ maxWidth: 760 }}>
                <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 26 }}>{isVenues ? 'Places' : isCreatives ? 'People' : 'The Edit'}</div>
                <h1 style={{ fontFamily: FD, fontSize: 'clamp(52px, 8.6vw, 118px)', fontWeight: 400, color: '#f5f0e8', margin: '0 0 30px', lineHeight: 0.97, letterSpacing: '-0.015em' }}>{category.label}</h1>
                <p className="clc-hero-desc" style={{ fontFamily: FU, fontSize: 'clamp(16px, 1.55vw, 19px)', fontWeight: 300, color: 'rgba(245,240,232,0.88)', margin: '0 0 28px', lineHeight: 1.78, maxWidth: 620 }}>{category.description}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 1.4vw, 22px)', marginBottom: 30, flexWrap: 'wrap' }}>
                  {[`${total} Editorial ${total === 1 ? 'Feature' : 'Features'}`, 'Updated Weekly', 'Curated by LWD'].map((t, i) => (
                    <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 'clamp(12px, 1.4vw, 22px)', fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.74)' }}>
                      {i > 0 && <span style={{ width: 3, height: 3, borderRadius: '50%', background: T.GOLD }} />}
                      {t}
                    </span>
                  ))}
                </div>
                <button onClick={scrollFeed} style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f5f0e8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(245,240,232,0.5)', padding: '16px 34px', cursor: 'pointer', backdropFilter: 'blur(3px)', transition: 'all 0.22s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.GOLD; e.currentTarget.style.borderColor = T.GOLD; e.currentTarget.style.color = '#1b1710'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(245,240,232,0.5)'; e.currentTarget.style.color = '#f5f0e8'; }}>
                  {ctaLabel}
                </button>
              </div>
            </div>
          </section>

          {/* ══ EXPLORE BY MOOD (venues only) ══ */}
          {isVenues && (
            <section style={{ background: T.PANEL, borderBottom: `1px solid ${T.LINE}`, padding: 'clamp(38px, 4vw, 56px) clamp(16px, 5vw, 80px)' }}>
              <div style={{ maxWidth: 1180, margin: '0 auto' }}>
                <div style={{ fontFamily: FU, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: T.FAINT, marginBottom: 34, textAlign: 'center' }}>Explore by Mood</div>
                <div className="clc-hscroll clc-mood-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, overflowX: 'auto' }}>
                  {MOODS.map((m) => {
                    const active = mood?.label === m.label;
                    return (
                      <button key={m.label} onClick={() => { setMood(active ? null : m); scrollFeed(); }}
                        className="clc-mood"
                        style={{ flex: '1 0 auto', minWidth: 92, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 13, background: 'none', border: 'none', padding: '6px 12px', cursor: 'pointer', color: active ? T.GOLD : T.MUTED }}>
                        <MoodIcon name={m.label} color={T.GOLD} size={30} />
                        <span style={{ fontFamily: FU, fontSize: 12.5, letterSpacing: '0.03em', color: active ? T.GOLD : T.MUTED, whiteSpace: 'nowrap' }}>{m.label}</span>
                        <span className="clc-mood-ul" style={active ? { width: 22 } : undefined} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ══ FEATURED COLLECTIONS ══ */}
          {collections.length > 0 && (
            <section style={{ padding: 'clamp(56px, 6.5vw, 90px) 0 clamp(48px, 5.5vw, 72px)', background: T.BG }}>
              <SectionDivider label="Featured Collections" T={T} />
              <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 clamp(24px, 5vw, 80px)' }}>
                {/* lead collection, dominant */}
                <button className="clc-coll" onClick={() => openCollection(collections[0])}
                  style={{ position: 'relative', display: 'block', width: '100%', overflow: 'hidden', borderRadius: 4, border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', marginBottom: 'clamp(16px, 1.8vw, 24px)' }}>
                  <div className="clc-coll-lead clc-card-img" style={{ backgroundImage: `url(${imgUrl(collections[0].image, { width: 1200 })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.12) 55%), linear-gradient(105deg, rgba(0,0,0,0.5) 0%, transparent 60%)' }} />
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(24px, 3.4vw, 48px)', maxWidth: 620 }}>
                    <div style={{ fontFamily: FU, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 12 }}>The Collection</div>
                    <div style={{ fontFamily: FD, fontSize: 'clamp(34px, 4.4vw, 64px)', fontWeight: 400, color: '#f5f0e8', lineHeight: 1.0, marginBottom: 14 }}>{collections[0].name}</div>
                    <p style={{ fontFamily: FU, fontSize: 'clamp(13px, 1.25vw, 16px)', fontWeight: 300, color: 'rgba(245,240,232,0.84)', margin: '0 0 18px', lineHeight: 1.66, maxWidth: 500 }}>{collections[0].intro}</p>
                    <span className="clc-read" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#f5f0e8' }}>{collections[0].count} {isCreatives ? 'Creatives' : 'Venues'} &nbsp;·&nbsp; Browse the Collection →</span>
                  </div>
                </button>
                {/* the rest, smaller, in a horizontal rail */}
                {collections.length > 1 && (
                  <div className="clc-hscroll" style={{ display: 'flex', gap: 'clamp(14px, 1.6vw, 22px)', overflowX: 'auto', scrollSnapType: 'x mandatory' }}>
                    {collections.slice(1).map((c, i) => (
                      <button key={c.slug} className="clc-coll" onClick={() => openCollection(c)}
                        style={{ position: 'relative', flex: '0 0 auto', width: 'clamp(166px, 17vw, 208px)', aspectRatio: '3 / 4', overflow: 'hidden', borderRadius: 3, border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', scrollSnapAlign: 'start', animation: `clcFade 0.5s ease-out ${i * 45}ms both` }}>
                        <div className="clc-card-img" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imgUrl(c.image, { width: 800 })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.05) 62%)' }} />
                        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(16px, 1.6vw, 22px)' }}>
                          <div style={{ fontFamily: FD, fontSize: 'clamp(19px, 1.7vw, 23px)', fontWeight: 400, color: '#f5f0e8', lineHeight: 1.12, marginBottom: 7 }}>{c.name}</div>
                          <div style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.66)' }}>{c.count} {isCreatives ? 'Creatives' : 'Venues'}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ══ EDITOR'S PICK ══ */}
          {picks.length >= 4 && (
            <section style={{ background: T.BAND, borderTop: `1px solid ${T.LINE}`, borderBottom: `1px solid ${T.LINE}`, padding: 'clamp(64px, 7.5vw, 104px) clamp(24px, 5vw, 80px)' }}>
              <SectionDivider label="Editor's Pick" T={T} />
              <div style={{ maxWidth: 1180, margin: '0 auto' }}>
                <div className="clc-editor" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.82fr', gap: 'clamp(28px, 3.4vw, 52px)', alignItems: 'center' }}>
                  {/* lead image */}
                  <a className="clc-card" href={hrefOf(picks[0])} onClick={cardNav(picks[0])} aria-label={picks[0].title} style={{ display: 'block', border: 'none', padding: 0, background: 'none', cursor: cardCursor(picks[0]), overflow: 'hidden', borderRadius: 3, aspectRatio: '4 / 3.25' }}>
                    <div className="clc-card-img" style={{ width: '100%', height: '100%', backgroundImage: `url(${imgUrl(picks[0].coverImage, { width: 1200, resize: 'contain' })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                  </a>
                  {/* centre text */}
                  <a href={hrefOf(picks[0])} onClick={cardNav(picks[0])} style={{ display: 'block', cursor: cardCursor(picks[0]), textDecoration: 'none' }}>
                    {locLabel(picks[0]) && <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 14 }}>{locLabel(picks[0])}</div>}
                    <h3 style={{ fontFamily: FD, fontSize: 'clamp(28px, 3.3vw, 44px)', fontWeight: 400, color: T.TEXT, margin: '0 0 16px', lineHeight: 1.08 }}>{picks[0].title}</h3>
                    <p style={{ fontFamily: FU, fontSize: 15, fontWeight: 300, color: T.MUTED, margin: '0 0 22px', lineHeight: 1.72, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{picks[0].excerpt}</p>
                    <span className="clc-read" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD }}>Read the Story →</span>
                  </a>
                  {/* right rail */}
                  <div className="clc-editor-rail" style={{ borderLeft: `1px solid ${T.LINE}`, paddingLeft: 'clamp(20px, 2vw, 32px)' }}>
                    {picks.slice(1, 4).map((p, i) => (
                      <a key={p.id || i} className="clc-card" href={hrefOf(p)} onClick={cardNav(p)} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 14, cursor: cardCursor(p), padding: '14px 0', borderTop: i === 0 ? 'none' : `1px solid ${T.LINE}`, textDecoration: 'none' }}>
                        <div style={{ overflow: 'hidden', borderRadius: 2, aspectRatio: '1 / 1' }}>
                          <div className="clc-card-img" style={{ width: '100%', height: '100%', backgroundImage: `url(${imgUrl(p.coverImage, { width: 800, resize: 'contain' })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          {locLabel(p) && <div style={{ fontFamily: FU, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 5 }}>{locLabel(p)}</div>}
                          <h4 style={{ fontFamily: FD, fontSize: 16, fontWeight: 400, color: T.TEXT, margin: 0, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.title}</h4>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ══ BROWSE THE COLLECTION ══ */}
          <div ref={feedRef}>
            <RefineFeed
              title="Browse the Collection" count={feed.length} noun={featureNoun} total={total}
              feed={feed} onGo={go} hrefOf={hrefOf} onCardNav={cardNav} T={T}
              refineOpen={refineOpen} setRefineOpen={setRefineOpen} refine={refine} setRefine={setRefine}
              isVenues={isVenues} isCreatives={isCreatives} isGeneric={isGeneric}
              countries={countries} styles={styles} creativeTypes={creativeTypes} venueTypes={VENUE_TYPES}
              mood={mood} clearMood={() => setMood(null)}
            />
          </div>

          {/* ══ CONTINUE EXPLORING (softer close into the footer) ══ */}
          <ContinueExploring current={category?.slug} chapterImages={chapterImages} onNavigate={onNavigateCategory} T={T} />
        </>
      )}
    </div>
  );
}

// ── Centered gold-rule section divider ────────────────────────────────────────
function SectionDivider({ label, T }) {
  return (
    <div style={{ maxWidth: 1180, margin: '0 auto clamp(30px, 3.5vw, 46px)', padding: '0 clamp(24px, 5vw, 80px)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
      <span style={{ flex: 1, maxWidth: 130, height: 1, background: `linear-gradient(to right, transparent, ${T.GOLD}99)` }} />
      <span style={{ fontFamily: FU, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: T.FAINT, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ flex: 1, maxWidth: 130, height: 1, background: `linear-gradient(to left, transparent, ${T.GOLD}99)` }} />
    </div>
  );
}

// ── Continue Exploring, chapter links that give the page a proper ending ─────
// Every slug here MUST be `eligible: true` in EDITORIAL_CATEGORY_HUBS
// (supabase/functions/_shared/searchManifest.js). Anything else is not prerendered,
// so it has no self-canonical and the edge middleware serves it as a 404 — meaning a
// chapter link here would point readers at a dead page. `real-weddings` was doing
// exactly that (0 published articles, held in the manifest); removed 2026-08-07 and
// replaced with `editorial`, which has 11.
const CHAPTERS = [
  { slug: 'our-venues', label: 'Our Venues' },
  { slug: 'our-creatives', label: 'Our Creatives' },
  { slug: 'editorial', label: 'Editorial' },
  { slug: 'destinations', label: 'Destinations' },
  { slug: 'perspectives', label: 'Perspectives' },
  { slug: 'fashion', label: 'Fashion' },
];
function ContinueExploring({ current, chapterImages = {}, onNavigate, T }) {
  const items = CHAPTERS.filter(c => c.slug !== lc(current));
  const goCat = (slug, e) => { if (onNavigate) { e.preventDefault(); onNavigate(slug); window.scrollTo({ top: 0 }); } };
  return (
    <section style={{ background: T.BAND, borderTop: `1px solid ${T.LINE}`, padding: 'clamp(60px, 7vw, 100px) clamp(24px, 5vw, 80px)' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <SectionDivider label="Continue Exploring" T={T} />
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 'clamp(12px, 1.4vw, 20px)' }} className="clc-chapters">
          {items.map(c => {
            const img = chapterImages[c.slug];
            return (
              <a key={c.slug} className="clc-coll clc-chapter-card" href={`/lwd-edit/category/${c.slug}`} onClick={e => goCat(c.slug, e)}
                style={{ position: 'relative', display: 'block', aspectRatio: '3 / 4', overflow: 'hidden', borderRadius: 3, textDecoration: 'none' }}>
                {img
                  ? <div className="clc-card-img" style={{ position: 'absolute', inset: 0, backgroundImage: `url(${imgUrl(img, { width: 800 })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                  : <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(150deg, ${T.BAND}, ${T.PANEL})` }} />}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.08) 62%)' }} />
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(14px, 1.4vw, 20px)', textAlign: 'center' }}>
                  <div style={{ fontFamily: FD, fontSize: 'clamp(15px, 1.4vw, 21px)', fontWeight: 400, color: '#f5f0e8', lineHeight: 1.12 }}>{c.label}</div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Simple feed section (collection view) ─────────────────────────────────────
function FeedSection({ title, count, noun, feedRef, feed, onGo, hrefOf, onCardNav, T }) {
  return (
    <section ref={feedRef} style={{ background: T.PANEL, padding: 'clamp(48px, 6vw, 80px) clamp(24px, 5vw, 80px)' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 34 }}>
          <span style={{ width: 22, height: 1, background: T.GOLD }} />
          <h2 style={{ fontFamily: FD, fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 400, color: T.TEXT, margin: 0 }}>{title}</h2>
          <span style={{ fontFamily: FU, fontSize: 11, color: T.FAINT }}>{count}</span>
        </div>
        <CardGrid feed={feed} onGo={onGo} hrefOf={hrefOf} onCardNav={onCardNav} T={T} />
      </div>
    </section>
  );
}

// ── Refine + feed ─────────────────────────────────────────────────────────────
function RefineFeed({ title, count, total, feed, onGo, hrefOf, onCardNav, T, refineOpen, setRefineOpen, refine, setRefine, isVenues, isCreatives, isGeneric, countries, styles, creativeTypes, venueTypes, mood, clearMood }) {
  const set = (k, v) => setRefine(r => ({ ...r, [k]: v }));
  // Sparse editorial chapters don't need a filter bar; venues/creatives always do.
  const showRefine = isVenues || isCreatives || total >= 12;
  const selStyle = { fontFamily: FU, fontSize: 13, color: T.TEXT, background: T.PANEL, border: `1px solid ${T.LINE}`, borderRadius: 2, padding: '11px 14px', cursor: 'pointer', minWidth: 150, outline: 'none' };
  const Select = ({ k, label, opts, disabled }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <span style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.FAINT }}>{label}</span>
      <select value={refine[k]} onChange={e => set(k, e.target.value)} disabled={disabled} style={{ ...selStyle, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <option value="">All</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  return (
    <section style={{ background: T.PANEL, borderTop: `1px solid ${T.LINE}`, padding: 'clamp(48px, 6vw, 76px) clamp(24px, 5vw, 80px)' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ marginBottom: 26 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
            <span style={{ width: 22, height: 1, background: T.GOLD }} />
            <h2 style={{ fontFamily: FD, fontSize: 'clamp(26px, 3.2vw, 40px)', fontWeight: 400, color: T.TEXT, margin: 0 }}>{title}</h2>
          </div>
          <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.GOLD, marginTop: 13, paddingLeft: 36 }}>{total} Editorial {total === 1 ? 'Feature' : 'Features'}</div>
          <p style={{ fontFamily: FU, fontSize: 'clamp(14px, 1.3vw, 16px)', fontWeight: 300, color: T.MUTED, margin: '16px 0 0', paddingLeft: 36, lineHeight: 1.7, maxWidth: 560 }}>Explore every {isCreatives ? 'creative ' : isVenues ? 'venue ' : ''}feature published by The LWD Edit, carefully curated to inspire extraordinary celebrations around the world.</p>
        </div>

        {/* Refine bar (collapsed), only where filtering is meaningful */}
        {showRefine && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <button onClick={() => setRefineOpen(o => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: FU, fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.TEXT, background: 'none', border: `1px solid ${T.LINE}`, borderRadius: 100, padding: '9px 20px', cursor: 'pointer' }}>
            Refine <span style={{ transform: refineOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 9 }}>▾</span>
          </button>
          {mood && <button onClick={clearMood} style={{ fontFamily: FU, fontSize: 11, color: T.GOLD, background: 'none', border: `1px solid ${T.GOLD}55`, borderRadius: 100, padding: '8px 15px', cursor: 'pointer' }}>{mood.label} ×</button>}
          <span style={{ fontFamily: FU, fontSize: 12, color: T.FAINT, marginLeft: 'auto' }}>{count} shown</span>
        </div>
        )}
        {showRefine && refineOpen && (
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end', padding: '22px 24px', background: T.BAND, border: `1px solid ${T.LINE}`, borderRadius: 4, marginBottom: 32, animation: 'clcFade 0.25s ease-out both' }}>
            {isCreatives ? (
              <>
                <Select k="creativeType" label="Creative Type" opts={creativeTypes} />
                <Select k="country" label="Country" opts={countries} />
                <Select k="style" label="Style" opts={styles.slice(0, 40)} />
              </>
            ) : isVenues ? (
              <>
                <Select k="country" label="Country" opts={countries} />
                <Select k="venueType" label="Venue Type" opts={venueTypes} />
                <Select k="exclusive" label="Exclusive Use" opts={['yes']} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <span style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.FAINT }}>Guest Count</span>
                  <select disabled style={{ ...selStyle, opacity: 0.4, cursor: 'not-allowed', minWidth: 150 }}><option>Coming soon</option></select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <span style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.FAINT }}>Season</span>
                  <select disabled style={{ ...selStyle, opacity: 0.4, cursor: 'not-allowed', minWidth: 150 }}><option>Coming soon</option></select>
                </div>
              </>
            ) : countries.length > 0 ? (
              <Select k="country" label="Place" opts={countries} />
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flex: 1, minWidth: 200 }}>
              <span style={{ fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.FAINT }}>Search {isCreatives ? 'creatives' : isVenues ? 'venues' : 'features'}</span>
              <input value={refine.search} onChange={e => set('search', e.target.value)} placeholder="Search..." style={{ ...selStyle, minWidth: 0, width: '100%' }} />
            </div>
          </div>
        )}

        <CardGrid feed={feed} onGo={onGo} hrefOf={hrefOf} onCardNav={onCardNav} T={T} />
      </div>
    </section>
  );
}

// ── Luxury editorial card grid, 18 per page + Load More (no numbered pages) ────
const PAGE = 18;
function CardGrid({ feed, onGo, hrefOf, onCardNav, T }) {
  const [visible, setVisible] = useState(PAGE);
  useEffect(() => { setVisible(PAGE); }, [feed]); // reset when a filter/collection changes
  if (!feed.length) return <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: FU, fontSize: 13, color: T.MUTED }}>No features match yet.</div>;
  const shown = feed.slice(0, visible);
  return (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', columnGap: 'clamp(28px, 3.2vw, 44px)', rowGap: 'clamp(44px, 4.8vw, 72px)' }}>
      {shown.map((p, i) => (
        <a key={p.id || i} className="clc-card" href={hrefOf ? hrefOf(p) : undefined} onClick={onCardNav ? onCardNav(p) : () => onGo(p)} style={{ display: 'block', cursor: (hrefOf && !hrefOf(p)) ? 'default' : 'pointer', textDecoration: 'none', animation: `clcFade 0.45s ease-out ${Math.min(i, 8) * 40}ms both` }}>
          <div style={{ overflow: 'hidden', borderRadius: 3, aspectRatio: '5 / 4', marginBottom: 18 }}>
            <div className="clc-card-img" style={{ width: '100%', height: '100%', backgroundImage: `url(${imgUrl(p.coverImage, { width: 800, resize: 'contain' })})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
          </div>
          {locLabel(p) && <div style={{ fontFamily: FU, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD, marginBottom: 10 }}>{locLabel(p)}</div>}
          <h3 style={{ fontFamily: FD, fontSize: 'clamp(21px, 1.95vw, 26px)', fontWeight: 400, color: T.TEXT, margin: '0 0 12px', lineHeight: 1.3 }}>{p.title}</h3>
          <p style={{ fontFamily: FU, fontSize: 13.5, fontWeight: 300, color: T.MUTED, margin: '0 0 14px', lineHeight: 1.65, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.excerpt}</p>
          <span className="clc-read" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: FU, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.GOLD }}>Read Story →</span>
        </a>
      ))}
    </div>
    {feed.length > visible && (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginTop: 'clamp(44px, 5vw, 68px)' }}>
        <span style={{ fontFamily: FU, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.FAINT }}>{visible} of {feed.length}</span>
        <button onClick={() => setVisible(v => v + PAGE)} style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.TEXT, background: 'none', border: `1px solid ${T.GOLD}`, borderRadius: 100, padding: '14px 40px', cursor: 'pointer', transition: 'all 0.22s' }}
          onMouseEnter={e => { e.currentTarget.style.background = T.GOLD; e.currentTarget.style.color = T.PANEL; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = T.TEXT; }}>
          Load More
        </button>
      </div>
    )}
    </>
  );
}
